import React from 'react';
import ReactDOM from 'react-dom';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';

/**
 * Mode + point-polarity toolbar shown during refine mode. Portaled
 * to <body> alongside {@link FloatingActionBar} so it docks at the
 * top of the viewport regardless of the stage's overflow clipping.
 *
 * Visible only while the user is in refine mode. Two segmented
 * controls:
 *   - mode:   Box (drag + resize) | Points (SAM3 prompts)
 *   - polarity (points mode only): Include (+) | Exclude (−)
 *
 * Also surfaces a live "N / 32" count + a "Clear" reset.
 */

export type RefineMode = 'box' | 'points';

interface RefineToolbarProps {
  visible: boolean;
  mode: RefineMode;
  onModeChange: (mode: RefineMode) => void;
  /** 1 = foreground/include, 0 = background/exclude. */
  nextPointLabel: 0 | 1;
  onPolarityChange: (label: 0 | 1) => void;
  pointCount: number;
  maxPoints: number;
  onClearPoints: () => void;
}

export const RefineToolbar: React.FC<RefineToolbarProps> = ({
  visible,
  mode,
  onModeChange,
  nextPointLabel,
  onPolarityChange,
  pointCount,
  maxPoints,
  onClearPoints,
}) => {
  if (Platform.OS !== 'web' || !visible) return null;
  const atCap = pointCount >= maxPoints;

  const bar = (
    <View style={styles.bar}>
      <View style={styles.segment}>
        <SegmentBtn
          active={mode === 'box'}
          onPress={() => onModeChange('box')}
          icon="crop-free"
          label="Box"
        />
        <SegmentBtn
          active={mode === 'points'}
          onPress={() => onModeChange('points')}
          icon="auto-fix-high"
          label="Points"
        />
      </View>

      {mode === 'points' ? (
        <>
          <View style={styles.divider} />
          <View style={styles.segment}>
            <SegmentBtn
              active={nextPointLabel === 1}
              onPress={() => onPolarityChange(1)}
              icon="add"
              label="Include"
              tone="include"
            />
            <SegmentBtn
              active={nextPointLabel === 0}
              onPress={() => onPolarityChange(0)}
              icon="remove"
              label="Exclude"
              tone="exclude"
            />
          </View>
          <View style={styles.countWrap}>
            <Text style={[styles.count, atCap && { color: fpColors.error }]}>
              {pointCount}
              <Text style={styles.countSlash}> / {maxPoints}</Text>
            </Text>
          </View>
          {pointCount > 0 ? (
            <Pressable
              onPress={onClearPoints}
              style={({ pressed }) => [
                styles.clearBtn,
                pressed && { opacity: 0.7 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Clear all points"
            >
              <MaterialIcons
                name="backspace"
                size={13}
                color={fpColors.onSurfaceVariant}
              />
              <Text style={styles.clearText}>Clear</Text>
            </Pressable>
          ) : null}
        </>
      ) : null}
    </View>
  );

  if (typeof document === 'undefined') return bar;
  return ReactDOM.createPortal(bar, document.body);
};

const SegmentBtn: React.FC<{
  active: boolean;
  onPress: () => void;
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  tone?: 'include' | 'exclude';
}> = ({ active, onPress, icon, label, tone }) => {
  const activeBg =
    tone === 'include'
      ? '#16a34a'
      : tone === 'exclude'
        ? '#dc2626'
        : fpColors.primary;
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.segBtn,
        active && { backgroundColor: activeBg },
        pressed && { opacity: 0.85 },
      ]}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: active }}
    >
      <MaterialIcons
        name={icon}
        size={14}
        color={active ? '#fff' : fpColors.onSurface}
      />
      <Text
        style={[
          styles.segLabel,
          { color: active ? '#fff' : fpColors.onSurface },
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  bar: {
    position: 'fixed' as any,
    top: 80,
    left: '50%',
    transform: [{ translateX: -180 }],
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 6,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 10px 30px rgba(31,26,34,0.22)',
          zIndex: 9999,
          maxWidth: 'calc(100vw - 24px)',
          flexWrap: 'wrap',
        } as any)
      : null),
  },
  segment: {
    flexDirection: 'row',
    gap: 2,
    padding: 2,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  segBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
  },
  segLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    letterSpacing: 0.1,
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: fpColors.outlineVariant,
  },
  countWrap: {
    paddingHorizontal: 4,
  },
  count: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onSurface,
  },
  countSlash: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  clearBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  clearText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
});
